import { Backdrop } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { format, parseISO } from "date-fns";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import DialogContentWrapper from "../../../../common/components/functional/dialogs/DialogContentWrapper";
import LoadingSpinner from "../../../../common/components/functional/loadingSpinner/LoadingSpinner";
import {
  selectWorkHistoryById,
  updateTalentWorkHistoryEntry,
} from "../../../workHistory/WorkHistoriesSlice";
import { UpdateTalentWorkHistoryEntry } from "../../../workHistory/types/commands";
import { EditMode } from "../../TalentDetails";
import CityControl from "./CityControl";
import DayCountControl from "./DayCountControl";
import DateControl from "./DateControl";
import JobDescriptionControl from "./JobDescriptionControl";
import WorkLocationControl from "./WorkLocationControl";
import WorkTitleControl from "./WorkTitleControl";
import { ExternalWorkHistoryEntry, FormValuesEdit } from "./types";
import { UnsavedChangesProvider } from "../../../../contexts/UnsavedChangesContext";
import { isoFormat } from "../../../../common/helpers/formatDateTime";
import { EezyWorkControl } from "./EezyWorkControl";

type Props = {
  onClose: () => void;
  editMode: EditMode;
};

const TalentEditWorkHistoryEntry = ({ onClose, editMode }: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { talentId } = useParams();

  const workHistoryEntry = useAppSelector(
    (state) =>
      selectWorkHistoryById(state, editMode.id) as ExternalWorkHistoryEntry,
  );
  const translatedWorkTitles = useAppSelector(
    (state) => state.localized.workTitle,
  );
  const showDayCount = !!workHistoryEntry.dayCountEditable;
  const isMigratedOrExpertAddedEezy =
    workHistoryEntry.isEezyWork && !!workHistoryEntry.editable;
  const hasEmployerDetails =
    !!workHistoryEntry.employerName || !!workHistoryEntry.employerBusinessId;

  const resolvedWorkTitleName =
    workHistoryEntry.workTitleName ||
    (workHistoryEntry.workTitleId
      ? translatedWorkTitles[workHistoryEntry.workTitleId] || ""
      : "");
  const showUnknownWorkTitleIdInfo =
    workHistoryEntry.isEezyWork &&
    (!workHistoryEntry.workTitleId ||
      !translatedWorkTitles[workHistoryEntry.workTitleId]);

  const defaultValues: FormValuesEdit = {
    isEezyWork: workHistoryEntry.isEezyWork,
    workLocationName: workHistoryEntry.workLocationName,
    workLocation: workHistoryEntry.workLocationId
      ? {
          id: workHistoryEntry.workLocationId,
          name: workHistoryEntry.workLocationName,
        }
      : null,
    workTitle: {
      id: workHistoryEntry.workTitleId,
      name: resolvedWorkTitleName,
    },
    jobDescription: workHistoryEntry.jobDescription || "",
    city: workHistoryEntry.city,
    startDate: parseISO(workHistoryEntry.startDate),
    endDate: workHistoryEntry.endDate
      ? parseISO(workHistoryEntry.endDate)
      : null,
    dayCount: workHistoryEntry.dayCount ?? null,
  };

  const methods = useForm<FormValuesEdit>({
    defaultValues: defaultValues,
    mode: "onChange",
  });

  const onSubmit = (formValues: FormValuesEdit) => {
    setIsLoading(true);

    const startDate = format(formValues.startDate, isoFormat);
    const endDate = formValues.endDate
      ? format(formValues.endDate, isoFormat)
      : undefined;

    if (talentId) {
      const selectedWorkTitle = formValues.workTitle;
      if (!selectedWorkTitle) return;
      const commandPayload: UpdateTalentWorkHistoryEntry = {
        id: workHistoryEntry.id,
        version: workHistoryEntry.version,
        workHistory: {
          talentId: talentId,
          workLocationId: workHistoryEntry.workLocationId,
          workLocationName: isMigratedOrExpertAddedEezy
            ? workHistoryEntry.workLocationName
            : formValues.workLocationName,
          workTitleId: selectedWorkTitle.id || null,
          workTitleName: selectedWorkTitle.name,
          ...(formValues.jobDescription && {
            jobDescription: formValues.jobDescription,
          }),
          city: formValues.city,
          startDate: startDate,
          endDate: endDate,
          customerOrgId: workHistoryEntry.customerOrgId,
          customerOrgName: workHistoryEntry.customerOrgName,
          employerId: workHistoryEntry.employerId,
          employerName: workHistoryEntry.employerName,
          employerBusinessId: workHistoryEntry.employerBusinessId,
        },
        ...(showDayCount && {
          dayCount: Math.max(0, Math.round(formValues.dayCount ?? 0)),
        }),
      };

      dispatch(updateTalentWorkHistoryEntry(commandPayload)).then((action) => {
        setIsLoading(false);

        const success = isFulfilled(action);

        if (success) {
          onClose();
        }

        return success;
      });
    }
  };

  const onSave = () => {
    methods.handleSubmit(onSubmit)();
  };

  const onCancel = () => {
    onClose();
  };

  const disabled =
    !methods.formState.isValid ||
    !methods.formState.isDirty ||
    isLoading;

  return (
    <FormProvider {...methods}>
      <DialogContentWrapper
        title={t("talentProfile:workExperience")}
        margin
        onSave={onSave}
        onCancel={onCancel}
        saveDisabled={disabled}
      >
        <Backdrop
          open={isLoading}
          sx={{
            backgroundColor: "rgba(255, 255, 255, 0.4)",
            zIndex: 1000,
          }}
        >
          <LoadingSpinner />
        </Backdrop>
        <UnsavedChangesProvider isDirty={methods.formState.isDirty}>
          <EezyWorkControl
            disabled
            employerName={workHistoryEntry.employerName}
            employerBusinessId={workHistoryEntry.employerBusinessId}
            showEmployerInfoIcon={hasEmployerDetails}
          />
          <WorkLocationControl
            disabled={isMigratedOrExpertAddedEezy}
            showUnknownWorkLocationIdInfo={
              workHistoryEntry.isEezyWork && !workHistoryEntry.workLocationId
            }
            unknownWorkLocationName={workHistoryEntry.workLocationName}
          />
          <WorkTitleControl
            defaultWorkTitle={defaultValues.workTitle}
            disabled={isMigratedOrExpertAddedEezy}
            showUnknownWorkTitleIdInfo={showUnknownWorkTitleIdInfo}
            unknownWorkTitleName={resolvedWorkTitleName}
          />
          <JobDescriptionControl />
          <CityControl disabled={isMigratedOrExpertAddedEezy} />
          {showDayCount && <DayCountControl />}
          <DateControl
            start={defaultValues.startDate}
            end={defaultValues.endDate}
            allowOngoingToggle={!isMigratedOrExpertAddedEezy}
          />
        </UnsavedChangesProvider>
      </DialogContentWrapper>
    </FormProvider>
  );
};

export default TalentEditWorkHistoryEntry;
